import { ConnectionOptions, GraphApiMethod } from '@/api/core';
import {
  IncidentIssueWithCache,
  MutationUpdateIncidentIssueBulkArgs,
  UpdateIncidentIssueBulkResponse,
  UpdateIncidentIssueInput,
  UpdateIncidentIssueValuesInput,
} from '@/models/gen/graphql';
import createGraphApiHook, { CreateGraphApiHookHook } from '@/hooks/createGraphApiHook';
import { getProperty, queryInput } from '@/utils';

import { TypedDocumentNode } from '@apollo/client';
import { UpdateIncidentIssue } from '@/features/IncidentHistory/api/queries/UpdateIncidentIssue';
import graphApi from '@/api';

// Service Function Signature
type ServiceMethodPayload = {
  [key in keyof IncidentIssueWithCache]?: any;
}[];
interface ServiceMethod {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<UpdateIncidentIssueBulkResponse>;
}

// UpdateIncidentIssue Service
export const { updateIncidentIssue, useUpdateIncidentIssue } = ((): {
  updateIncidentIssue: ServiceMethod;
  useUpdateIncidentIssue: CreateGraphApiHookHook<ServiceMethod>;
} => {
  const [fetch] = graphApi(UpdateIncidentIssue).map(
    (fn: GraphApiMethod<TypedDocumentNode>): ServiceMethod =>
      async (
        payload?: Partial<UpdateIncidentIssueValuesInput & { id: string }>[],
        options?: ConnectionOptions
      ): Promise<UpdateIncidentIssueBulkResponse> => {
        debug.log('updateIncidentIssue,updateIncidentIssue.input', 'INPUT', payload);
        const updates: UpdateIncidentIssueInput[] =
          payload
            ?.filter(({ id }) => id)
            ?.map(
              ({ id, ...value }): UpdateIncidentIssueInput => ({
                query: [
                  {
                    id: queryInput(id),
                  },
                ],
                value: {
                  crewInfo: value?.crewInfo || null,
                  crewInvolved: value?.crewInvolved || null,
                  description: value?.description || null,
                  dispatcherId: value?.dispatcherId || null,
                  driverId: value?.driverId || null,
                  incidentDatetime: value?.incidentDatetime || null,
                  incidentOccurance: value?.incidentOccurance || null,
                  incidentOccuranceOther: value?.incidentOccuranceOther || null,
                  incidentWitnesses: value?.incidentWitnesses || null,
                  managerId: value?.managerId || null,
                  medicalAttentionType: value?.medicalAttentionType || null,
                  medicalInjuries: value?.medicalInjuries || null,
                  medicalNeeded: value?.medicalNeeded || null,
                  outboundDatetime: value?.outboundDatetime || null,
                  outboundFlightNumber: value?.outboundFlightNumber || null,
                  outboundFlightName: value?.outboundFlightName || null,
                  propertyDamage: value?.propertyDamage || null,
                  propertyDamageDescription: value?.propertyDamageDescription || null,
                  recoveryInfo: value?.recoveryInfo || null,
                  samsaraCameraWorking: value?.samsaraCameraWorking || null,
                  station: value?.station || null,
                  traceId: value?.traceId || null,
                  tripId: value?.tripId || null,
                  vehicleId: value?.vehicleId || null,
                },
              })
            ) ?? [];
        const args: MutationUpdateIncidentIssueBulkArgs = {
          input: {
            updates,
          },
        };
        debug.log('updateIncidentIssue,updateIncidentIssue.payload', 'PAYLOAD', args);
        const opts = { merge: options?.merge ? 'updateIncidentIssueBulk' : undefined };
        const res = await fn(args, opts);
        debug.log('updateIncidentIssue,updateIncidentIssue.response', 'RESPONSE', res);
        return getProperty('updateIncidentIssueBulk', res);
      }
  );
  const useService = createGraphApiHook(fetch);
  return {
    updateIncidentIssue: fetch,
    useUpdateIncidentIssue: useService,
  };
})();
